// sequencePlayer.js - исправленная версия
import { getCurrentTempo, getFullMelodySequence, getMeasuresForBlock } from './notes.js';
import { playBlock, stopBlockPlayback } from './blockPlayer.js';
import { setActiveMelodyBlock } from './guitar.js';
import * as Tone from 'tone';
import { threeJSBand } from "./threejsBand";
import { calculateNoteDuration, beatsToSeconds, beatsToMilliseconds, MUSIC_CONSTANTS } from './utils/musicUtils.js';
import { getSafeStartTime, TIME_CONSTANTS } from './utils/timeUtils.js';

// Константы для SequencePlayer
const SEQUENCE_CONFIG = {
    DEFAULT_START_DELAY: 0.1,
    CLEANUP_INTERVAL: 5
};

// Специальное исключение для принудительного прерывания
class PlaybackInterruptedError extends Error {
    constructor(message = 'Playback interrupted') {
        super(message);
        this.name = 'PlaybackInterruptedError';
    }
}

class SequencePlayer {
    constructor() {
        this.isPlaying = false;
        this.currentSequenceIndex = -1;
        this.currentBlockId = null;
        this.shouldStop = false;
        this.lastCleanup = 0;
        this.pauseTimeoutId = null; // Для отмены таймаута паузы
        this.abortController = null; // Для принудительного прерывания
    }

    /**
     * Воспроизводит полную последовательность блоков с учетом пауз
     * @param {number|null} scheduledStartTime - абсолютное время старта (Tone.now())
     */
    async playFullSequence(scheduledStartTime = null) {
        if (this.isPlaying) {
            await this.stop();
            return;
        }

        this.isPlaying = true;
        this.shouldStop = false;
        this.abortController = new AbortController(); // Для принудительного прерывания
        const fullSequence = getFullMelodySequence();

        // Проверяем, что последовательность не пуста
        if (!fullSequence || fullSequence.length === 0) {
            console.error('Ошибка: последовательность пуста или не загружена');
            this.isPlaying = false;
            throw new Error('Последовательность не загружена. Попробуйте нажать кнопку еще раз.');
        }

        try {
            this.clearMovingNotes();

            const startTime = scheduledStartTime || (Tone.now() + SEQUENCE_CONFIG.DEFAULT_START_DELAY);
            let nextBlockStartTime = startTime;

            for (let i = 0; i < fullSequence.length; i++) {
                // Принудительная проверка остановки перед каждой итерацией
                this.checkStop();

                this.currentSequenceIndex = i;
                const block = fullSequence[i];
                this.updateUI(i, fullSequence.length, block);

                if (block.isPause) {
                    nextBlockStartTime = await this.handlePauseBlock(nextBlockStartTime);
                    // Проверка после await
                    this.checkStop();
                } else {
                    nextBlockStartTime = await this.handleMusicBlockSequence(block, nextBlockStartTime);
                    // Проверка после await
                    this.checkStop();
                }

                this.performPeriodicCleanup();
            }
        } catch (error) {
            // Игнорируем ошибки прерывания и ручной остановки - это нормальное поведение
            if (error instanceof PlaybackInterruptedError || error.message === 'Playback stopped manually') {
                // Это нормальное прерывание, просто выходим
                return;
            }
            console.error('Ошибка воспроизведения последовательности:', error);
            throw error;
        } finally {
            this.abortController = null;
            await this.cleanup();
            // Сбрасываем флаг isPlayAllMode после завершения последовательности
            // Это важно, чтобы при следующем запуске блока синтезатор и фоновая музыка работали
            if (window.isPlayAllMode) {
                window.isPlayAllMode = false;
                if (window.app && window.app.playAllStats) {
                    window.app.playAllStats.isTracking = false;
                }
            }
        }
    }

    /**
     * Проверяет флаг остановки и выбрасывает исключение для принудительного прерывания
     */
    checkStop() {
        if (this.shouldStop) {
            throw new PlaybackInterruptedError('Playback stopped manually');
        }
    }

    async handlePauseBlock(nextBlockStartTime) {
        this.checkStop();
        const pauseDuration = this.calculatePauseDuration() / MUSIC_CONSTANTS.MILLISECONDS_PER_SECOND;
        await this.handlePause();
        this.checkStop();
        return nextBlockStartTime + pauseDuration;
    }

    async handleMusicBlockSequence(block, nextBlockStartTime) {
        this.checkStop();
        const blockStartTime = nextBlockStartTime;
        const currentTime = Tone.now();
        const adjustedBlockStartTime = getSafeStartTime(blockStartTime, currentTime);
        const timeDrift = adjustedBlockStartTime - blockStartTime;

        this.checkStop();
        await this.prepareBlockNotes(block.id, adjustedBlockStartTime);
        this.checkStop();
        
        await this.handleMusicBlock(block, adjustedBlockStartTime);
        this.checkStop();

        const blockDuration = this.calculateBlockDuration(block.id);
        return blockStartTime + blockDuration + timeDrift;
    }

    performPeriodicCleanup() {
        if (Tone.now() - this.lastCleanup > SEQUENCE_CONFIG.CLEANUP_INTERVAL) {
            this.cleanupStuckNotes();
            this.lastCleanup = Tone.now();
        }
    }

    async prepareBlockNotes(blockId, blockStartTime) {
        this.checkStop();
        
        const blockMeasures = getMeasuresForBlock(blockId);
        if (!blockMeasures || blockMeasures.length === 0) return;

        const allBlockNotes = [];
        let timeCursor = 0;
        const tempo = getCurrentTempo() || MUSIC_CONSTANTS.DEFAULT_TEMPO;
        const currentTime = Tone.now();
        const actualBlockStartTime = getSafeStartTime(blockStartTime, currentTime);

        // Используем обычный цикл вместо forEach для возможности прерывания
        for (let i = 0; i < blockMeasures.length; i++) {
            this.checkStop();
            const measure = blockMeasures[i];
            
            for (let j = 0; j < measure.length; j++) {
                this.checkStop();
                const chordGroup = measure[j];
                
                const isPause = chordGroup[0]?.pause === "true";
                if (isPause) {
                    timeCursor += this.processPauseInBlock(chordGroup[0]);
                } else {
                    const notes = this.processChordGroup(chordGroup, allBlockNotes);
                    const duration = this.calculateChordGroupDuration(chordGroup[0]);
                    const groupStartTime = actualBlockStartTime + beatsToSeconds(timeCursor, tempo);
                    
                    // Минимальное время полета для нот (в секундах)
                    const MIN_NOTE_TRAVEL_TIME = 1.5;
                    // Каждая группа нот имеет свой startTime для равномерной скорости
                    const noteStartTime = groupStartTime - MIN_NOTE_TRAVEL_TIME;
                    
                    if (j < 3) {
                        console.log(`🎶 SequencePlayer chord ${j}:`, {
                            timeCursor: timeCursor.toFixed(3),
                            groupStartTime: groupStartTime.toFixed(3),
                            noteStartTime: noteStartTime.toFixed(3),
                            travelTime: (groupStartTime - noteStartTime).toFixed(3)
                        });
                    }
                    
                    this.checkStop();
                    this.createMovingNotes(notes, noteStartTime, groupStartTime);
                    timeCursor += duration;
                }
            }
        }

        this.checkStop();
        this.focusCameraOnBlock(allBlockNotes);
        
        // После добавления всех нот обновляем hammer эффекты
        if (threeJSBand) {
            this.checkStop();
            threeJSBand.updateHammerEffects();
        }
    }

    processPauseInBlock(pauseData) {
        return calculateNoteDuration(pauseData.duration, pauseData.dotted || false);
    }

    processChordGroup(chordGroup, allBlockNotes) {
        const notes = [];
        chordGroup.forEach(noteData => {
            if (noteData.noteName) {
                allBlockNotes.push(noteData);
                notes.push(noteData);
            }
        });
        return notes;
    }

    calculateChordGroupDuration(chordData) {
        return calculateNoteDuration(chordData.duration, chordData.dotted);
    }

    createMovingNotes(notes, startTime, targetTime) {
        notes.forEach((noteData) => {
            // Ноты добавляются, hammer эффект создастся позже через updateHammerEffects
            threeJSBand.addMovingNote(noteData, startTime, targetTime, true, null);
        });
    }

    focusCameraOnBlock(allBlockNotes) {
        if (threeJSBand && allBlockNotes.length > 0) {
            threeJSBand.animateCameraToBlock(allBlockNotes);
        }
    }

    calculateBlockDuration(blockId) {
        const blockMeasures = getMeasuresForBlock(blockId);
        if (blockMeasures && blockMeasures.length > 0) {
            const tempo = getCurrentTempo() || MUSIC_CONSTANTS.DEFAULT_TEMPO;
            const beats = blockMeasures.length * MUSIC_CONSTANTS.BEATS_PER_MEASURE;
            return beatsToSeconds(beats, tempo);
        }
        return 0;
    }

    async handleMusicBlock(block, sequenceStartTime) {
        this.checkStop();
        this.currentBlockId = block.id;
        setActiveMelodyBlock(block.id);

        try {
            await playBlock(block.id, sequenceStartTime);
            // Проверяем после завершения блока
            this.checkStop();
        } catch (error) {
            // Игнорируем ошибки прерывания и ручной остановки - это нормальное поведение
            if (error instanceof PlaybackInterruptedError || error.message === 'Playback stopped manually') {
                // Это нормальное прерывание, просто выходим
                return;
            }
            console.error(`Ошибка воспроизведения блока ${block.id}:`, error);
            throw error;
        }
    }

    async handlePause() {
        const duration = this.calculatePauseDuration();

        return new Promise((resolve, reject) => {
            this.checkStop();

            // Сохраняем ID таймаута для возможности отмены
            this.pauseTimeoutId = setTimeout(() => {
                this.pauseTimeoutId = null;
                // Проверяем еще раз перед разрешением промиса
                if (this.shouldStop) {
                    reject(new PlaybackInterruptedError('Playback stopped manually'));
                } else {
                    resolve();
                }
            }, duration);
        });
    }

    calculatePauseDuration() {
        const tempo = getCurrentTempo() || MUSIC_CONSTANTS.DEFAULT_TEMPO;
        return beatsToMilliseconds(MUSIC_CONSTANTS.BEATS_PER_MEASURE, tempo);
    }

    cleanupStuckNotes() {
        if (threeJSBand) {
            // Используем внутренний метод threeJSBand для очистки зависших нот
            threeJSBand.cleanupStuckNotes();
        }
    }

    updateUI(currentIndex, total, block) {
        this.highlightSequenceBlock(currentIndex, true);

        const progressElement = document.getElementById('progressDisplay');
        if (progressElement) {
            if (block.isPause) {
                progressElement.textContent = `Пауза ${currentIndex + 1}/${total}`;
            } else {
                progressElement.textContent = `Блок ${block.id} | ${currentIndex + 1}/${total}`;
            }
        }
    }

    highlightSequenceBlock(index, isActive) {
        const elements = document.querySelectorAll('.sequence-block');
        elements.forEach((el, i) => {
            if (isActive && i === index) {
                el.classList.add('active');
                
                // Автоскролл к активному блоку в полной последовательности
                const wrapper = el.closest('.full-melody-sequence-wrapper');
                if (wrapper) {
                    el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                        inline: 'center'
                    });
                }
            } else {
                el.classList.remove('active');
            }
        });
    }

    async stop() {
        this.shouldStop = true;
        this.isPlaying = false;
        
        // Принудительно прерываем таймаут паузы, если он активен
        if (this.pauseTimeoutId !== null) {
            clearTimeout(this.pauseTimeoutId);
            this.pauseTimeoutId = null;
        }
        
        // Прерываем AbortController для принудительного прерывания
        if (this.abortController) {
            this.abortController.abort();
        }
        
        // Немедленно очищаем все ноты перед остановкой блоков
        this.clearMovingNotes();
        
        await stopBlockPlayback(false);
        await this.cleanup();
    }

    async cleanup() {
        this.highlightSequenceBlock(this.currentSequenceIndex, false);
        this.currentSequenceIndex = -1;
        this.currentBlockId = null;
        this.isPlaying = false;
        this.shouldStop = false;

        // Снимаем флаги playing и active у всех melody-block
        document.querySelectorAll('.melody-block').forEach(block => {
            block.classList.remove('playing');
            block.classList.remove('active');
        });

        this.clearMovingNotes();
        this.resetProgressDisplay();
    }

    clearMovingNotes() {
        if (threeJSBand) {
            threeJSBand.clearAllMovingNotes();
        }
    }

    resetProgressDisplay() {
        const progressElement = document.getElementById('progressDisplay');
        if (progressElement) {
            progressElement.textContent = '0/0';
        }
    }
}

/** Экземпляр проигрывателя полной последовательности (оркеструет блоки и паузы) */
export const sequencePlayer = new SequencePlayer();